import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';

async function _safeReadText(resp) {
  try {
    return await resp.text();
  } catch (e) {
    return '';
  }
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [isCourier, setIsCourier] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [courierProfile, setCourierProfile] = useState(null);

  const registerInFlightRef = useRef(false);
  const refreshRoleRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const refreshRole = useCallback(async (forcedSession = null) => {
    const activeSession = forcedSession || session;
    const token = activeSession?.access_token;

    if (!token) {
      setIsCourier(false);
      setIsAdmin(false);
      setCourierProfile(null);
      setProfile(null);
      return { isCourier: false, isAdmin: false, courierProfile: null };
    }

    const url = `${API_BASE_URL}/auth/me`;
    console.log(' AuthContext: role refresh start url=', url);
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resp.ok) {
        const t = await _safeReadText(resp);
        console.warn(' AuthContext: role refresh failed status=', resp.status, 'body=', t);
        setIsCourier(false);
        setIsAdmin(false);
        setCourierProfile(null);
        setProfile(null);
        return { isCourier: false, isAdmin: false, courierProfile: null };
      }

      const data = await resp.json();
      const courier = !!data?.is_courier;
      const admin = !!data?.is_admin;
      setIsCourier(courier);
      setIsAdmin(admin);
      setCourierProfile(data?.courier || null);
      setProfile(data?.profile || null);
      console.log(' AuthContext: role refresh ok isCourier=', courier, 'isAdmin=', admin);
      return { isCourier: courier, isAdmin: admin, courierProfile: data?.courier || null };
    } catch (e) {
      console.warn(' AuthContext: role refresh error', e);
      setIsCourier(false);
      setIsAdmin(false);
      setCourierProfile(null);
      setProfile(null);
      return { isCourier: false, isAdmin: false, courierProfile: null };
    }
  }, [API_BASE_URL, session]);

  const updateProfile = useCallback(
    async ({ name, phone }) => {
      const token = session?.access_token;
      if (!token) throw new Error('NOT_AUTHENTICATED');

      const resp = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, phone }),
      });

      if (!resp.ok) {
        if (resp.status === 401) throw new Error('NOT_AUTHENTICATED');
        const t = await _safeReadText(resp);
        throw new Error(t || 'PROFILE_UPDATE_FAILED');
      }

      const data = await resp.json().catch(() => null);
      setProfile(data?.profile || null);
      return data;
    },
    [API_BASE_URL, session?.access_token]
  );

  useEffect(() => {
    refreshRoleRef.current = refreshRole;
  }, [refreshRole]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) {
        setSession(null);
        setUser(null);
        setIsCourier(false);
        setIsAdmin(false);
        setCourierProfile(null);
        setProfile(null);
        setIsLoading(false);
        return;
      }

      setSession(data.session || null);
      setUser(data.session?.user || null);
      try {
        await refreshRoleRef.current?.(data.session || null);
      } catch (e) {
        // ignore
      }
      setIsLoading(false);
    };

    init();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log(' AuthContext: onAuthStateChange event=', event);
      setSession(newSession || null);
      setUser(newSession?.user || null);
      try {
        await refreshRoleRef.current?.(newSession || null);
      } catch (e) {
        // ignore
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const safeEmail = (email || '').trim().toLowerCase();
    console.log(' AuthContext: login start email=', safeEmail);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.warn(' AuthContext: login failed', {
          email: safeEmail,
          message: error?.message,
          status: error?.status,
          name: error?.name,
        });
        return { success: false, error: error.message || 'Giriş başarısız' };
      }

      console.log(' AuthContext: login ok email=', safeEmail, 'has_session=', !!data?.session);
      setSession(data.session || null);
      setUser(data.session?.user || null);
      const role = await refreshRole(data.session || null);
      return { success: true, ...role };
    } catch (e) {
      const msg =
        (e && typeof e === 'object' && (e.message || e.detail))
          ? (e.detail || e.message)
          : String(e || '');
      console.error(' AuthContext: login exception email=' + safeEmail, e);
      return { success: false, error: msg || 'Giriş başarısız' };
    }
  };

  const requestWithXhr = useCallback(async (url, bodyObj) => {
    return await new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-Type', 'application/json');

        xhr.onload = () => {
          const status = xhr.status;
          const ok = status >= 200 && status < 300;
          const responseText = xhr.responseText || '';

          let retryAfter = null;
          try {
            const ra = xhr.getResponseHeader('Retry-After');
            retryAfter = ra ? Number(ra) : null;
          } catch (e) {
            retryAfter = null;
          }

          resolve({ ok, status, responseText, retryAfter });
        };

        xhr.onerror = () => {
          reject(new Error('Network error'));
        };

        xhr.send(JSON.stringify(bodyObj));
      } catch (e) {
        reject(e);
      }
    });
  }, []);

  const register = async (userData) => {
    if (registerInFlightRef.current) {
      return { success: false, error: 'İşlem devam ediyor. Lütfen bekleyin.' };
    }

    registerInFlightRef.current = true;
    try {
      const { ok, responseText, retryAfter } = await requestWithXhr(`${API_BASE_URL}/auth/register`, {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
      });

      if (!ok) {
        let detail = 'Üyelik oluşturulamadı';
        try {
          const raw = responseText;
          if (raw) {
            try {
              const err = JSON.parse(raw);
              detail = err?.detail || err?.message || raw || detail;
            } catch (e) {
              detail = raw;
            }
          }
        } catch (e) {
          // ignore
        }
        return { success: false, error: detail, retryAfter };
      }

      const data = responseText ? JSON.parse(responseText) : {};

      if (data?.pendingEmailConfirmation) {
        return { success: true, pendingEmailConfirmation: true };
      }

      if (data?.session?.access_token && data?.session?.refresh_token) {
        const { data: sessionData, error } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (error) {
          return { success: false, error: error.message };
        }

        setSession(sessionData.session || null);
        setUser(sessionData.session?.user || null);
        return { success: true, pendingEmailConfirmation: false };
      }

      return { success: true, pendingEmailConfirmation: true };
    } catch (e) {
      const msg =
        (e && typeof e === 'object' && (e.message || e.detail))
          ? (e.detail || e.message)
          : String(e || '');
      return { success: false, error: msg || 'Üyelik oluşturulamadı' };
    } finally {
      registerInFlightRef.current = false;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setIsCourier(false);
    setIsAdmin(false);
    setCourierProfile(null);
    setProfile(null);
  };

  const forgotPassword = async (email) => {
    try {
      const { ok, responseText, retryAfter } = await requestWithXhr(`${API_BASE_URL}/auth/forgot-password`, { email });

      if (!ok) {
        let detail = 'İşlem başarısız';
        try {
          const raw = responseText;
          if (raw) {
            try {
              const err = JSON.parse(raw);
              detail = err?.detail || err?.message || raw || detail;
            } catch (e) {
              detail = raw;
            }
          }
        } catch (e) {
          // ignore
        }
        return { success: false, error: detail, retryAfter };
      }

      return { success: true };
    } catch (e) {
      const msg =
        (e && typeof e === 'object' && (e.message || e.detail))
          ? (e.detail || e.message)
          : String(e || '');
      return { success: false, error: msg || 'İşlem başarısız' };
    }
  };

  const updateUser = () => {};

  const getAccessToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || null;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isAuthenticated: !!session,
        isLoading,
        login,
        logout,
        register,
        isCourier,
        isAdmin,
        courierProfile,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};